/**
 * Estado de sessão em memória — access token NUNCA em localStorage/sessionStorage.
 * Variável de módulo: some ao fechar a aba, reobtida via POST /auth/refresh (cookie HttpOnly).
 * Ver seguranca-frontend-web/SKILL.md.
 */
export interface SessionState {
  accessToken: string | null;
  expiresAt: number | null;
  user: SessionUser | null;
  /**
   * Token CSRF da sessão (double-submit). Vem do CORPO de /auth/session e /auth/refresh —
   * a SPA roda em origem distinta do BFF e não consegue ler `document.cookie` de lá.
   */
  csrf: string | null;
}

export interface SessionUser {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  /** Persona dona da sessão — o BFF decide por qual realm a sessão foi aberta. */
  readonly persona: 'operacional' | 'tecnicos' | 'clinica';
  readonly roles: readonly string[];
  /** Claims de autorização aditivos; ausentes durante a janela de migração. */
  readonly permissions?: readonly string[];
}

let state: SessionState = { accessToken: null, expiresAt: null, user: null, csrf: null };
const listeners = new Set<(state: SessionState) => void>();

export function getSession(): SessionState {
  return state;
}

export function setSession(next: SessionState): void {
  if (isSessionBlocked() && (next.accessToken !== null || next.user !== null || next.csrf !== null)) return;
  state = next;
  for (const listener of listeners) listener(state);
}

export function clearSession(): void {
  setSession({ accessToken: null, expiresAt: null, user: null, csrf: null });
}

export function onSessionChange(listener: (state: SessionState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isSessionExpiringSoon(bufferMs = 60_000): boolean {
  if (state.expiresAt === null) return false;
  return Date.now() >= state.expiresAt - bufferMs;
}

// Somente uma preferência de acesso, sem identidade ou credenciais persistidas.
const LOGOUT_PENDING_KEY = 'dt_logout_pending';
let pendingInMemory = false;
let blockedInMemory = false;

export function isSessionBlocked(): boolean {
  try { return blockedInMemory || isLogoutPending() || localStorage.getItem('dt_session_closed') === '1'; }
  catch { return blockedInMemory || isLogoutPending(); }
}

/** Só uma nova troca PKCE bem-sucedida autoriza uma identidade após uma saída explícita. */
export function resumeSession(): void {
  completeLogout();
  blockedInMemory = false;
  try { localStorage.removeItem('dt_session_closed'); } catch { /* Estado em memória. */ }
}

export function isLogoutPending(): boolean {
  try { return pendingInMemory || localStorage.getItem(LOGOUT_PENDING_KEY) === '1'; }
  catch { return pendingInMemory; }
}

export function completeLogout(): void {
  pendingInMemory = false;
  try { localStorage.removeItem(LOGOUT_PENDING_KEY); } catch { /* Armazenamento indisponível. */ }
}


export function beginLogout(): void {
  pendingInMemory = true;
  blockedInMemory = true;
  try { localStorage.setItem('dt_session_closed', '1'); } catch { /* Estado em memória. */ }
  try { localStorage.setItem(LOGOUT_PENDING_KEY, '1'); } catch { /* Mantém o bloqueio em memória. */ }
  clearSession();
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if ((event.key === 'dt_session_closed' || event.key === LOGOUT_PENDING_KEY) && event.newValue === '1') clearSession();
  });
}
